import { toRaw } from 'vue';
import {
  type DMAcceptorStartBlocker,
  type DMAcceptorStartBlockersResponse,
  type DMAcceptorStatus,
  DMAcceptorStatusResponse,
  type DMAcceptorType,
  type DMGetBlockersResponse,
  Money,
} from '@nsftx/systems-sdk';
import { logService } from '@/common/services/logger';
import deviceManagementService from '@/modules/device-management/services/deviceManagementService';
import BaseError from '@/common/errors/BaseError';
import { errorParser } from '@/common/services/error-parser';
import { storeToRefs } from 'pinia';
import { useNotificationsStore } from '@/common/stores/notifications';
import { useDmAcceptorsStore } from './dmAcceptorsStore';
import { showDmAcceptorStartBlockerErrors, handleAcceptorsChangeStateError } from '../helpers';
import { AcceptorStateChangeEvent, AcceptorType } from '../types';

const LOG_PREFIX = '[dmAcceptorsService]';
interface ChangeAcceptorsStatusParams {
  acceptor: DMAcceptorType;
  status: DMAcceptorStatus;
}

let money: Money;
let isMoneyReady: boolean = false;
/**
 * List of blockers that DM expects the Terminal to act upon
 * by stopping the acceptors when they are present and starting them when they are not,
 * in order to be able to delegate the decision of stopping/starting
 * the acceptors to the Terminal instead of the underlying DM Application runtime.
 * If there are blockers outside of this list,
 * the Terminal will not be able to decide whether to stop/start the acceptors
 * and will rely on DM Application runtime to do so.
 */
const BLOCKERS_ACTED: DMAcceptorStartBlocker[] = [
  'doors_open',
  'network_problem',
  'printer_problem',
  'login_data_reject_money',
];

/**
 * Blockers that DM will reject Start for. Sending AllAcceptorsStart is useless
 * while any of these are present — Terminal must not attempt Start.
 * `faulty_acceptor` is known by Terminal locales but not yet in SDK union.
 */
const BLOCKERS_NON_IGNORABLE: readonly string[] = [
  'bill_disabled_on_dm',
  'coin_disabled_on_dm',
  'money_manager_unavailable',
  'faulty_acceptor',
];

const hasBlockersFromActedList = (
  blockers: DMAcceptorStartBlocker[],
): boolean => blockers.some((blocker) => BLOCKERS_ACTED.includes(blocker));

const hasNonIgnorableBlockers = (
  blockers: DMAcceptorStartBlocker[],
): boolean => blockers.some((blocker) => BLOCKERS_NON_IGNORABLE.includes(blocker));

const getDmAcceptorStartBlockers = (
  devices: DMAcceptorStatusResponse[],
) => {
  const list = Array.isArray(devices) ? devices : [];
  const billBlockers = list.find((d) => d.device === 'bill')?.blockers;
  const coinBlockers = list.find((d) => d.device === 'coin')?.blockers;

  return {
    bill: Array.isArray(billBlockers) ? billBlockers : [],
    coin: Array.isArray(coinBlockers) ? coinBlockers : [],
  };
};

const getMoneyInstance = () => {
  if (isMoneyReady) return money;

  throw (new BaseError(
    'Money is not yet initialized',
    'T_DM_MONEY_NOT_INITIALIZED',
  ));
};

const changeAcceptorStatusAll = (start: boolean) => getMoneyInstance()
  .changeAcceptorStatusAll(start);

const changeAcceptorStatus = ({
  acceptor, status,
}: ChangeAcceptorsStatusParams) => getMoneyInstance().changeAcceptorStatus(acceptor, status);

const initDmMoney = async () => {
  const DM_MONEY_TIMEOUT = 60_000;

  logService.info(`${LOG_PREFIX} Acceptor service is using DM Application runtime.`, {
    code: 'T_DM_MONEY_INIT',
  });

  const core = deviceManagementService.getCore();

  return new Promise<void>((resolve, reject) => {
    const rejectTimeout = window.setTimeout(() => {
      reject(new BaseError(
        `${LOG_PREFIX} Money instance did not receive ready event on init.`,
        'T_DM_MONEY_NOT_READY',
      ));
    }, DM_MONEY_TIMEOUT);

    money = new Money(core, false);
    money.on('ready', () => {
      logService.info(`${LOG_PREFIX} Money instance ready event received`, {
        code: 'T_DM_MONEY_READY',
      });
      window.clearTimeout(rejectTimeout);
      isMoneyReady = true;
      resolve();
    });
  });
};

const requestBlockers = () => getMoneyInstance().requestBlockers();

const getBlockers = () => getMoneyInstance().getBlockers();

const handleDmAcceptorStartBlockersEvents = (
  acceptor: AcceptorType,
  response: DMAcceptorStartBlockersResponse,
) => {
  const dmAcceptorsStore = useDmAcceptorsStore();
  const { acceptorStartBlockersExist, acceptorStartBlockers } = storeToRefs(dmAcceptorsStore);
  const { blockers } = response;
  const blockersNormalized = Array.isArray(blockers) ? blockers : [];

  logService.info(`${LOG_PREFIX} Updated list of acceptor start blockers received on ${acceptor} event.`, {
    code: `T_ACCEPTORS_${acceptor.toUpperCase()}_START_BLOCKERS_CHANGE_EVENT`,
    details: {
      currentBlockers: toRaw(acceptorStartBlockers.value),
      acceptorStartBlockersExist: toRaw(acceptorStartBlockersExist.value),
      blockers,
    },
  });

  dmAcceptorsStore.setAcceptorStartBlockers(blockersNormalized, acceptor);

  if (!acceptorStartBlockers.value[acceptor].length) {
    logService.info(`${LOG_PREFIX} Message to start ${acceptor} acceptor on blockers event.`, {
      code: 'T_ACCEPTORS_START_ON_EVENT',
      details: {
        acceptor,
        status: 'start',
      },
    });
    changeAcceptorStatus({ acceptor, status: 'start' })
      .then(() => {
        const notificationsStore = useNotificationsStore();
        notificationsStore.closeNotificationWithId(`acceptors_start_blocker_${acceptor}`);

        logService.info(`${LOG_PREFIX} Message to start ${acceptor} acceptor on blockers event was successful.`, {
          code: 'T_ACCEPTORS_START_ON_EVENT_SUCCESS',
        });
      }).catch((err) => {
        logService.error(`${LOG_PREFIX} Message to start ${acceptor} acceptor on blockers event failed.`, {
          code: 'T_ACCEPTORS_START_ON_EVENT_ERROR',
          ...errorParser.parseUpstream(err),
        });
      });
    return;
  }

  // stop acceptors if we are in charge of stopping them instead of underlying service
  if (hasBlockersFromActedList(blockersNormalized)) {
    logService.info(`${LOG_PREFIX} Message to stop ${acceptor} acceptor on blockers event.`, {
      code: 'T_ACCEPTORS_STOP_ON_EVENT',
      details: {
        acceptor,
        status: 'stop',
      },
    });
    changeAcceptorStatus({ acceptor, status: 'stop' })
      .then(() => {
        const notificationsStore = useNotificationsStore();
        notificationsStore.closeNotificationWithId(`acceptors_stop_blocker_${acceptor}`);

        logService.info(`${LOG_PREFIX} Message to stop acceptors on ${acceptor} event was successful.`, {
          code: 'T_ACCEPTORS_STOP_ON_EVENT_SUCCESS',
        });
      }).catch((err) => {
        logService.error(`${LOG_PREFIX} Message to stop acceptors on ${acceptor} event failed.`, {
          code: 'T_ACCEPTORS_STOP_ON_EVENT_ERROR',
          ...errorParser.parseUpstream(err),
        });
      });
  }

  showDmAcceptorStartBlockerErrors();
};

const registerDmAcceptorStartBlockersEvents = () => {
  money.on('coinStartBlockers', (response) => handleDmAcceptorStartBlockersEvents('coin', response));
  money.on('billStartBlockers', (response) => handleDmAcceptorStartBlockersEvents('bill', response));
};

const handleDmAcceptorsChangeStateSuccess = (
  response: DMAcceptorStatusResponse[],
  acceptMoney: boolean,
  event: AcceptorStateChangeEvent,
) => {
  const dmAcceptorsStore = useDmAcceptorsStore();
  const { acceptorStartBlockersExist, acceptorStartBlockers } = storeToRefs(dmAcceptorsStore);
  const blockers = getDmAcceptorStartBlockers(response);
  const action = acceptMoney ? 'START' : 'STOP';

  dmAcceptorsStore.setAcceptorStartBlockers(blockers);

  // if it's STOP acceptors action or it's START action but there are no blockers
  if (!acceptMoney || !acceptorStartBlockersExist.value) {
    logService.info(`[AcceptorsService] Message to ${action} acceptors successfully sent.`, {
      code: 'T_ACCEPTORS_CHANGE_ACTION_SUCCESS',
      details: {
        acceptMoney,
        blockers: toRaw(acceptorStartBlockers.value),
      },
    });
    event.resolve();

    return;
  }

  const message = '[AcceptorsService] Message to START acceptors was not sent. Blockers are present.';
  const code = 'T_ACCEPTORS_CHANGE_ACTION_ERROR';

  showDmAcceptorStartBlockerErrors();

  logService.error(message, {
    code,
    details: {
      blockers: toRaw(acceptorStartBlockers.value),
    },
  });

  event.reject(new BaseError(
    message,
    code,
    {
      context: {
        blockers: acceptorStartBlockers.value,
      },
    },
  ));
};

/**
 * Fetches current DPM blockers, then sends start/stop.
 * STOP always sends stop. START is rejected when Terminal-acted blockers are present.
 */
const changeAcceptorsStateOnInitialization = (
  acceptMoney: boolean,
  event: AcceptorStateChangeEvent,
) => {
  getBlockers()
    .then((response: DMGetBlockersResponse) => {
      const billBlockers = Array.isArray(response?.bill_blockers) ? response.bill_blockers : [];
      const coinBlockers = Array.isArray(response?.coin_blockers) ? response.coin_blockers : [];

      logService.info('[dmAcceptorsService] Received get blockers on state initialization response', {
        code: 'T_ACCEPTORS_GET_BLOCKERS_ON_INIT_SUCCESS',
        details: {
          billBlockers,
          coinBlockers,
        },
      });

      const dmAcceptorsStore = useDmAcceptorsStore();
      dmAcceptorsStore.setAcceptorStartBlockers({
        bill: [...billBlockers],
        coin: [...coinBlockers],
      });

      // STOP always sends stop.
      // START is skipped for Terminal-acted blockers (business choice) and for
      // non-ignorable DM blockers (Start would be rejected by DM anyway).
      const shouldSkipStart = hasBlockersFromActedList(billBlockers)
        || hasBlockersFromActedList(coinBlockers)
        || hasNonIgnorableBlockers(billBlockers)
        || hasNonIgnorableBlockers(coinBlockers);

      if (acceptMoney && shouldSkipStart) {
        const message = '[dmAcceptorsService] Message to START acceptors on initialization was not sent. Blockers are present.';
        const code = 'T_ACCEPTORS_INIT_CHANGE_ACTION_ERROR';

        showDmAcceptorStartBlockerErrors();

        logService.error(message, {
          code,
          details: {
            billBlockers,
            coinBlockers,
          },
        });

        event.reject(new BaseError(
          message,
          code,
          {
            context: {
              billBlockers,
              coinBlockers,
            },
          },
        ));
        return undefined;
      }

      return changeAcceptorStatusAll(acceptMoney);
    })
    .then((response) => {
      if (!response) {
        return;
      }

      handleDmAcceptorsChangeStateSuccess(response, acceptMoney, event);
    })
    .catch((err) => {
      handleAcceptorsChangeStateError(acceptMoney, event, err);
    });
};

export {
  getDmAcceptorStartBlockers,
  getMoneyInstance,
  initDmMoney,
  changeAcceptorStatus,
  changeAcceptorStatusAll,
  changeAcceptorsStateOnInitialization,
  registerDmAcceptorStartBlockersEvents,
  hasBlockersFromActedList,
  hasNonIgnorableBlockers,
  handleDmAcceptorsChangeStateSuccess,
  requestBlockers,
  getBlockers,
};
